/**
 * Author: Romain Hapi Sr.
 * Version: 1.0
 * Date: 2026-09-10
 * Project: Prenim Digital
 * Copyright: Prenim UK
 */
import { Workflow, Compass, Layers, Code2, GitBranch, Network } from 'lucide-react';

/**
 * Catalogue des six services : icone, adresse et couleur, en un seul endroit.
 *
 * L'accueil et la page Services declaraient chacun leur propre liste, avec des
 * icones differentes pour le meme service (une fusee ici, un nuage la) et des
 * degrades sans rapport. Les libelles, eux, restent dans les traductions.
 *
 * Les classes Tailwind sont ecrites en entier : Tailwind analyse le source
 * comme du texte et ne verrait pas une classe assemblee a l'execution.
 */
export const SERVICES = [
  {
    key: 'digitalTransformation',
    slug: 'digital-transformation',
    Icon: Workflow,
    bg: 'bg-amber-50',
    hover: 'hover:bg-amber-100/70',
    icon: 'text-amber-600',
    rule: 'bg-amber-500',
    dot: 'bg-amber-500',
    features: [
      { fr: 'Stratégie de transformation', en: 'Transformation strategy', ar: 'استراتيجية التحول' },
      { fr: 'Modernisation des processus', en: 'Process modernization', ar: 'تحديث العمليات' },
      { fr: 'Innovation digitale', en: 'Digital innovation', ar: 'الابتكار الرقمي' },
    ],
  },
  {
    key: 'consulting',
    slug: 'consulting',
    Icon: Compass,
    bg: 'bg-sky-50',
    hover: 'hover:bg-sky-100/70',
    icon: 'text-sky-600',
    rule: 'bg-sky-500',
    dot: 'bg-sky-500',
    features: [
      { fr: 'Audit IT complet', en: 'Complete IT audit', ar: 'مراجعة تكنولوجيا المعلومات الكاملة' },
      { fr: 'Architecture solution', en: 'Solution architecture', ar: 'بنية الحلول' },
      { fr: 'Optimisation des coûts', en: 'Cost optimization', ar: 'تحسين التكلفة' },
    ],
  },
  {
    key: 'integration',
    slug: 'systems-integration',
    Icon: Layers,
    bg: 'bg-violet-50',
    hover: 'hover:bg-violet-100/70',
    icon: 'text-violet-600',
    rule: 'bg-violet-500',
    dot: 'bg-violet-500',
    features: [
      { fr: 'API Gateway', en: 'API Gateway', ar: 'بوابة API' },
      { fr: 'Intégration ESB', en: 'ESB Integration', ar: 'تكامل ESB' },
      { fr: 'Migration de données', en: 'Data migration', ar: 'ترحيل البيانات' },
    ],
  },
  {
    key: 'development',
    slug: 'development',
    Icon: Code2,
    bg: 'bg-emerald-50',
    hover: 'hover:bg-emerald-100/70',
    icon: 'text-emerald-600',
    rule: 'bg-emerald-500',
    dot: 'bg-emerald-500',
    features: [
      { fr: 'Applications web modernes', en: 'Modern web applications', ar: 'تطبيقات ويب حديثة' },
      { fr: 'Applications desktop', en: 'Desktop applications', ar: 'تطبيقات سطح المكتب' },
      { fr: 'API RESTful', en: 'RESTful APIs', ar: 'RESTful APIs' },
    ],
  },
  {
    key: 'devops',
    slug: 'devops',
    Icon: GitBranch,
    bg: 'bg-rose-50',
    hover: 'hover:bg-rose-100/70',
    icon: 'text-rose-600',
    rule: 'bg-rose-500',
    dot: 'bg-rose-500',
    features: [
      { fr: 'CI/CD Pipelines', en: 'CI/CD Pipelines', ar: 'خطوط إنتاج CI/CD' },
      { fr: 'Kubernetes & Docker', en: 'Kubernetes & Docker', ar: 'Kubernetes و Docker' },
      { fr: 'Infrastructure as Code', en: 'Infrastructure as Code', ar: 'البنية التحتية ككود' },
    ],
  },
  {
    key: 'networking',
    slug: 'networking',
    Icon: Network,
    bg: 'bg-indigo-50',
    hover: 'hover:bg-indigo-100/70',
    icon: 'text-indigo-600',
    rule: 'bg-indigo-500',
    dot: 'bg-indigo-500',
    features: [
      { fr: 'Architecture réseau', en: 'Network architecture', ar: 'بنية الشبكة' },
      { fr: 'Sécurité réseau', en: 'Network security', ar: 'أمن الشبكة' },
      { fr: 'SD-WAN Solutions', en: 'SD-WAN Solutions', ar: 'حلول SD-WAN' },
    ],
  },
];

/** Effet de decollage au survol, partage par les cartes cliquables. */
export const LIFT =
  'transition-[transform,box-shadow,background-color] duration-300 ease-out ' +
  'hover:z-10 hover:-translate-y-1.5 hover:shadow-lg hover:shadow-slate-900/10 ' +
  'focus-visible:z-10 focus-visible:-translate-y-1.5 focus-visible:shadow-lg ' +
  'motion-reduce:transform-none motion-reduce:hover:translate-y-0';
